import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Search, 
  ExternalLink, 
  MoreHorizontal,
  Pencil,
  Trash2,
  Users,
  Phone,
  Mail,
  Briefcase,
  X,
  Tag,
  Megaphone,
  Bell,
  MapPin,
  Filter,
  Link2,
  Target,
  RefreshCw,
  Webhook
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ContactsDialog } from '../components/clients/ContactsDialog';
import SEOProviderDialog from '../components/clients/SEOProviderDialog';
import GlobalSEOSettings from '../components/clients/GlobalSEOSettings';
import CPATargetDialog from '../components/clients/CPATargetDialog';
import GBPLinkDialog from '../components/clients/GBPLinkDialog';
import ZonesCiblesDialog from '../components/clients/ZonesCiblesDialog';
import MarchesDialog from '../components/clients/MarchesDialog';
import PortalClientDialog from '../components/clients/PortalClientDialog';
import OfflineConversionActionDialog from '../components/clients/OfflineConversionActionDialog';
import WebhookLeadsDialog from '../components/clients/WebhookLeadsDialog';
import ConnectionHealthBanner from '../components/clients/ConnectionHealthBanner';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Clients() {
  const { user } = useAuth();
  const { mode } = useMode();
  const [clients, setClients] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    contact_email: '',
    contact_phone: '',
    consultant_id: '',
    seo_consultant_id: '',
    sea_consultant_id: '',
    notes: ''
  });

  // Contacts management
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [contactsClient, setContactsClient] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', function: '', phone: '' });
  const [editingContact, setEditingContact] = useState(null);

  // CPA target dialog
  const [cpaDialogOpen, setCpaDialogOpen] = useState(false);
  const [cpaClient, setCpaClient] = useState(null);
  const [gbpDialogOpen, setGbpDialogOpen] = useState(false);
  const [gbpClient, setGbpClient] = useState(null);
  const [zonesDialogOpen, setZonesDialogOpen] = useState(false);
  const [zonesClient, setZonesClient] = useState(null);
  const [marchesDialogOpen, setMarchesDialogOpen] = useState(false);
  const [marchesClient, setMarchesClient] = useState(null);
  const [portalDialogOpen, setPortalDialogOpen] = useState(false);
  const [portalClient, setPortalClient] = useState(null);
  const [offlineActionDialogOpen, setOfflineActionDialogOpen] = useState(false);
  const [offlineActionClient, setOfflineActionClient] = useState(null);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [webhookClient, setWebhookClient] = useState(null);

  // Lead keywords management
  const [keywordsDialogOpen, setKeywordsDialogOpen] = useState(false);
  const [keywordsClient, setKeywordsClient] = useState(null);
  const [seoDialogOpen, setSeoDialogOpen] = useState(false);
  const [seoClient, setSeoClient] = useState(null);
  const [leadKeywords, setLeadKeywords] = useState('');

  // Memoized parsed keywords (used in preview chips + on save)
  const parsedLeadKeywords = useMemo(
    () => leadKeywords.split(/[\n,;]/).map(k => k.trim()).filter(Boolean),
    [leadKeywords],
  );
  const [savingKeywords, setSavingKeywords] = useState(false);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    // Fetch clients & consultants independently so a failure on one doesn't
    // wipe out the other (defensive against transient 401s / API errors).
    try {
      const clientsRes = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(clientsRes.data);
    } catch (e) {
      console.error('Clients fetch failed', e);
      toast.error(e.response?.data?.detail || "Impossible de charger les clients");
    }
    try {
      const consultantsRes = await axios.get(`${API}/consultants`, { withCredentials: true });
      setConsultants(consultantsRes.data);
    } catch (e) {
      console.error('Consultants fetch failed', e);
      // Silent — consultants are not strictly required to render the clients list
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Coerce empty consultant IDs to null so backend sees them as "unassigned"
      const payload = {
        ...formData,
        consultant_id: formData.consultant_id || null,
        seo_consultant_id: formData.seo_consultant_id || null,
        sea_consultant_id: formData.sea_consultant_id || null,
      };
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/clients`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      website: client.website,
      industry: client.industry || '',
      contact_email: client.contact_email || '',
      contact_phone: client.contact_phone || '',
      consultant_id: client.consultant_id || '',
      seo_consultant_id: client.seo_consultant_id || '',
      sea_consultant_id: client.sea_consultant_id || '',
      notes: client.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    try {
      await axios.delete(`${API}/clients/${clientId}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      website: '',
      industry: '',
      contact_email: '',
      contact_phone: '',
      consultant_id: '',
      seo_consultant_id: '',
      sea_consultant_id: '',
      notes: ''
    });
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = (
      client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      client.website.toLowerCase().includes(searchQuery.toLowerCase())
    );
    // Filter by mode: SEA mode only shows clients with sea_active=true
    // SEO mode shows clients with seo_active=true (default true for backward compat)
    const matchesMode = mode === 'sea' ? client.sea_active === true : client.seo_active !== false;
    return matchesSearch && matchesMode;
  });

  // Contacts functions
  const openContactsDialog = async (client) => {
    setContactsClient(client);
    setContactsDialogOpen(true);
    setContactsLoading(true);
    try {
      const { data } = await axios.get(`${API}/clients/${client.id}/contacts`, { withCredentials: true });
      setContacts(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setContactsLoading(false);
    }
  };

  const resetContactForm = () => {
    setContactForm({ name: '', email: '', function: '', phone: '' });
    setEditingContact(null);
  };

  const handleSaveContact = async () => {
    if (!contactForm.name || !contactForm.email) return;
    try {
      if (editingContact) {
        await axios.put(`${API}/clients/${contactsClient.id}/contacts/${editingContact.id}`, contactForm, { withCredentials: true });
      } else {
        await axios.post(`${API}/clients/${contactsClient.id}/contacts`, contactForm, { withCredentials: true });
      }
      const { data } = await axios.get(`${API}/clients/${contactsClient.id}/contacts`, { withCredentials: true });
      setContacts(data);
      resetContactForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({ name: contact.name, email: contact.email, function: contact.function || '', phone: contact.phone || '' });
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/clients/${contactsClient.id}/contacts/${contactId}`, { withCredentials: true });
      setContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const openKeywordsDialog = (client) => {
    setKeywordsClient(client);
    const existing = client.lead_keywords || [];
    setLeadKeywords(existing.join('\n'));
    setKeywordsDialogOpen(true);
  };

  const toggleClientMode = async (client, field) => {
    // field is 'seo_active' or 'sea_active'
    const newValue = !(field === 'seo_active' ? (client.seo_active !== false) : client.sea_active);
    try {
      await axios.put(
        `${API}/clients/${client.id}/stats-settings`,
        { [field]: newValue },
        { withCredentials: true }
      );
      toast.success(
        field === 'sea_active'
          ? (newValue ? `${client.name} activé pour le SEA` : `${client.name} désactivé du SEA`)
          : (newValue ? `${client.name} activé pour le SEO` : `${client.name} désactivé du SEO`)
      );
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const toggleAutoTracking = async (client) => {
    // default true → toggle to false, then back to true
    const current = client.auto_tracking_enabled !== false;
    const newValue = !current;
    try {
      await axios.put(
        `${API}/clients/${client.id}/stats-settings`,
        { auto_tracking_enabled: newValue },
        { withCredentials: true }
      );
      toast.success(
        newValue
          ? `${client.name} : tracking auto hebdo activé`
          : `${client.name} : tracking auto hebdo désactivé`
      );
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleSaveKeywords = async () => {
    if (!keywordsClient) return;
    setSavingKeywords(true);
    try {
      const keywords = parsedLeadKeywords;
      await axios.put(`${API}/clients/${keywordsClient.id}/lead-keywords`, { lead_keywords: keywords }, { withCredentials: true });
      toast.success(`${keywords.length} mots-cles enregistres pour ${keywordsClient.name}`);
      setKeywordsDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingKeywords(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="clients-page">
        {user?.role === 'admin' && <GlobalSEOSettings />}

        {/* Connection-health banner — shows expired Google Ads OAuth */}
        {user?.role === 'admin' && <ConnectionHealthBanner />}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Clients {mode === 'sea' && <span className="text-orange-600 text-lg font-normal ml-2">— Mode SEA</span>}
            </h1>
            <p className="text-neutral-500 mt-1">
              {mode === 'sea'
                ? `Clients actifs en SEA (${filteredClients.length}/${clients.length}). Utilisez "Actif SEA" dans le menu pour activer/désactiver un client.`
                : 'Gérez vos clients et leurs sites web'}
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#002FA7] hover:bg-[#002277] text-white"
                data-testid="add-client-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un client
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingClient ? 'Modifier le client' : 'Nouveau client'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom du client *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-1"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website">Site web *</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://example.com"
                      required
                      className="mt-1"
                      data-testid="client-website-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="industry">Secteur</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="mt-1"
                      data-testid="client-industry-input"
                    />
                  </div>
                  <div></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="seo_consultant">Consultant SEO</Label>
                    <Select
                      value={formData.seo_consultant_id || '__none__'}
                      onValueChange={(value) => setFormData({ ...formData, seo_consultant_id: value === '__none__' ? '' : value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="client-seo-consultant-select">
                        <SelectValue placeholder="Aucun" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Aucun</SelectItem>
                        {consultants.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="sea_consultant">Consultant SEA</Label>
                    <Select
                      value={formData.sea_consultant_id || '__none__'}
                      onValueChange={(value) => setFormData({ ...formData, sea_consultant_id: value === '__none__' ? '' : value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="client-sea-consultant-select">
                        <SelectValue placeholder="Aucun" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Aucun</SelectItem>
                        {consultants.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-neutral-500 mt-1">Indépendant du consultant SEO. Laisser vide si même personne pour SEO et SEA.</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_email">Email de contact</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="mt-1"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_phone">Téléphone</Label>
                    <Input
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="mt-1"
                      data-testid="client-phone-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#002FA7] hover:bg-[#002277]"
                    data-testid="save-client-btn"
                  >
                    {editingClient ? 'Enregistrer' : 'Créer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Rechercher un client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-clients-input"
          />
        </div>

        {/* Clients Table */}
        <div className="bg-white border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center" data-testid="clients-loading">
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-clients">
              <p className="text-neutral-500">Aucun client trouvé</p>
            </div>
          ) : (
            <table className="w-full data-table" data-testid="clients-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Site web</th>
                  <th>Secteur</th>
                  <th>Consultant SEO</th>
                  <th>Consultant SEA</th>
                  <th>Statut</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr key={client.id} data-client-row={client.id} className="transition-all">
                    <td>
                      <Link 
                        to={`/clients/${client.id}`}
                        className="font-medium text-neutral-900 hover:text-[#002FA7]"
                        data-testid={`client-link-${client.id}`}
                      >
                        {client.name}
                      </Link>
                    </td>
                    <td>
                      <a 
                        href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-neutral-600 hover:text-[#002FA7] flex items-center gap-1"
                      >
                        {client.website.replace(/^https?:\/\//, '')}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="text-neutral-600">{client.industry || '-'}</td>
                    <td className="text-neutral-600 text-sm">
                      {client.seo_consultant_name || client.consultant_name || <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="text-neutral-600 text-sm">
                      {client.sea_consultant_name || <span className="text-neutral-300">—</span>}
                    </td>
                    <td>
                      <span className={`text-xs font-mono px-2 py-1 rounded ${
                        client.status === 'active' 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        {client.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`client-menu-${client.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openContactsDialog(client)}>
                            <Users className="w-4 h-4 mr-2" />
                            Contacts
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openKeywordsDialog(client)}>
                            <Tag className="w-4 h-4 mr-2" />
                            Mots-cles leads
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSeoClient(client); setSeoDialogOpen(true); }}
                                            data-testid={`client-seo-provider-${client.id}`}>
                            <Search className="w-4 h-4 mr-2" />
                            Provider SEO
                            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono ${
                              client.seo_provider === 'haloscan'
                                ? (client.haloscan_configured ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-amber-100 text-amber-700')
                                : 'bg-neutral-100 text-neutral-600'
                            }`}>
                              {client.seo_provider === 'haloscan'
                                ? (client.haloscan_configured ? 'Haloscan' : 'Halo!')
                                : ({ semrush: 'SEMrush', ubersuggest: 'Ubersuggest' }[client.seo_provider] || 'DataForSEO')}
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleClientMode(client, 'seo_active')}
                                            data-testid={`toggle-seo-${client.id}`}>
                            <Search className="w-4 h-4 mr-2" />
                            Actif SEO
                            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono ${
                              client.seo_active !== false ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-neutral-100 text-neutral-500'
                            }`}>
                              {client.seo_active !== false ? 'ON' : 'OFF'}
                            </span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleClientMode(client, 'sea_active')}
                                            data-testid={`toggle-sea-${client.id}`}>
                            <Megaphone className="w-4 h-4 mr-2" />
                            Actif SEA
                            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono ${
                              client.sea_active === true ? 'bg-orange-100 text-orange-700' : 'bg-neutral-100 text-neutral-500'
                            }`}>
                              {client.sea_active === true ? 'ON' : 'OFF'}
                            </span>
                          </DropdownMenuItem>
                          {user?.role === 'admin' && (
                            <DropdownMenuItem
                              onClick={() => toggleAutoTracking(client)}
                              data-testid={`toggle-auto-tracking-${client.id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Tracking auto hebdo
                              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                client.auto_tracking_enabled !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'
                              }`}>
                                {client.auto_tracking_enabled !== false ? 'ON' : 'OFF'}
                              </span>
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => { setCpaClient(client); setCpaDialogOpen(true); }}
                                              data-testid={`cpa-target-${client.id}`}>
                              <Bell className="w-4 h-4 mr-2" />
                              Cible CPA
                              {client.target_cpa > 0 && (
                                <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                  client.cpa_alert_enabled ? 'bg-orange-100 text-orange-700' : 'bg-neutral-100 text-neutral-500'
                                }`}>
                                  {client.target_cpa}€
                                </span>
                              )}
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => { setZonesClient(client); setZonesDialogOpen(true); }}
                                              data-testid={`zones-target-${client.id}`}>
                              <MapPin className="w-4 h-4 mr-2" />
                              Zones cibles
                              {(client.target_zones?.length > 0) && (
                                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono bg-emerald-100 text-emerald-700">
                                  {client.target_zones.length}
                                </span>
                              )}
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => { setMarchesClient(client); setMarchesDialogOpen(true); }}
                                              data-testid={`marches-${client.id}`}>
                              <Filter className="w-4 h-4 mr-2" />
                              Marchés (exclus / focus)
                              {((client.excluded_terms?.length || 0) + (client.focus_terms?.length || 0) > 0) && (
                                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono bg-amber-100 text-amber-700">
                                  {(client.excluded_terms?.length || 0)}–{(client.focus_terms?.length || 0)}
                                </span>
                              )}
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => { setPortalClient(client); setPortalDialogOpen(true); }}
                                              data-testid={`portal-${client.id}`}>
                              <Link2 className="w-4 h-4 mr-2" />
                              Portail client
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && client.google_ads_customer_id && (
                            <DropdownMenuItem onClick={() => { setOfflineActionClient(client); setOfflineActionDialogOpen(true); }}
                                              data-testid={`offline-action-${client.id}`}>
                              <Target className="w-4 h-4 mr-2" />
                              Conversion offline (Google Ads)
                              {client.offline_conversion_action_id && (
                                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">✓</span>
                              )}
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => { setGbpClient(client); setGbpDialogOpen(true); }}
                                              data-testid={`gbp-link-${client.id}`}>
                              <MapPin className="w-4 h-4 mr-2" />
                              Fiche GBP
                              {client.gbp_location_id && (
                                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono bg-emerald-100 text-emerald-700">
                                  Liée
                                </span>
                              )}
                            </DropdownMenuItem>
                          )}
                          {user?.role === 'admin' && (
                            <DropdownMenuItem onClick={() => { setWebhookClient(client); setWebhookDialogOpen(true); }}
                                              data-testid={`webhook-leads-${client.id}`}>
                              <Webhook className="w-4 h-4 mr-2" />
                              Webhook leads
                              {client.webhook_configured && (
                                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono bg-violet-100 text-violet-700">
                                  Actif
                                </span>
                              )}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleEdit(client)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Contacts Management Dialog */}
        <ContactsDialog
          open={contactsDialogOpen}
          onOpenChange={(open) => { setContactsDialogOpen(open); if (!open) { resetContactForm(); setContactsClient(null); } }}
          client={contactsClient}
          contacts={contacts}
          contactsLoading={contactsLoading}
          contactForm={contactForm}
          setContactForm={setContactForm}
          editingContact={editingContact}
          onSave={handleSaveContact}
          onEdit={handleEditContact}
          onDelete={handleDeleteContact}
          onResetForm={resetContactForm}
        />

        <SEOProviderDialog
          open={seoDialogOpen}
          onOpenChange={(o) => { setSeoDialogOpen(o); if (!o) setSeoClient(null); }}
          client={seoClient}
          onSaved={() => fetchData()}
        />

        <CPATargetDialog
          open={cpaDialogOpen}
          onOpenChange={(o) => { setCpaDialogOpen(o); if (!o) setCpaClient(null); }}
          client={cpaClient}
          onSaved={() => fetchData()}
        />

        <GBPLinkDialog
          open={gbpDialogOpen}
          onOpenChange={(o) => { setGbpDialogOpen(o); if (!o) setGbpClient(null); }}
          client={gbpClient}
          onLinked={() => fetchData()}
        />

        {/* Zones cibles Dialog */}
        <ZonesCiblesDialog
          open={zonesDialogOpen}
          onOpenChange={(o) => { setZonesDialogOpen(o); if (!o) setZonesClient(null); }}
          client={zonesClient}
          onSaved={() => fetchData()}
        />

        {/* Marchés (exclus / focus) Dialog */}
        <MarchesDialog
          open={marchesDialogOpen}
          onOpenChange={(o) => { setMarchesDialogOpen(o); if (!o) setMarchesClient(null); }}
          client={marchesClient}
          onSaved={() => fetchData()}
        />

        {/* Portail client Dialog */}
        <PortalClientDialog
          open={portalDialogOpen}
          onOpenChange={(o) => { setPortalDialogOpen(o); if (!o) setPortalClient(null); }}
          client={portalClient}
          onSaved={() => fetchData()}
        />

        {/* Offline Conversion Action (Google Ads) Dialog */}
        <OfflineConversionActionDialog
          open={offlineActionDialogOpen}
          onOpenChange={(o) => { setOfflineActionDialogOpen(o); if (!o) setOfflineActionClient(null); }}
          client={offlineActionClient}
          onSaved={() => fetchData()}
        />

        {/* Webhook Leads Dialog */}
        <WebhookLeadsDialog
          open={webhookDialogOpen}
          onOpenChange={(o) => { setWebhookDialogOpen(o); if (!o) { setWebhookClient(null); fetchData(); } }}
          client={webhookClient}
        />

        {/* Lead Keywords Dialog */}
        <Dialog open={keywordsDialogOpen} onOpenChange={setKeywordsDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#002FA7]" />
                Mots-cles de detection — {keywordsClient?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-sm text-neutral-600">
                <p>Ces mots-cles sont utilises pour attribuer automatiquement les formulaires Gmail a ce client.</p>
                <p className="text-xs text-neutral-400 mt-1">Un par ligne. La recherche est insensible a la casse. Le domaine du site web est ajoute automatiquement.</p>
              </div>
              <div>
                <Label>Termes de detection (un par ligne)</Label>
                <textarea
                  value={leadKeywords}
                  onChange={(e) => setLeadKeywords(e.target.value)}
                  placeholder={`Ex:\n${keywordsClient?.name?.toLowerCase() || 'nom-client'}\n${keywordsClient?.website?.replace(/https?:\/\/(www\.)?/, '').split('/')[0]?.split('.')[0] || 'domaine'}\nalias-client`}
                  className="mt-1 flex min-h-[120px] w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm font-mono placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7] focus-visible:ring-offset-1"
                  rows={6}
                  data-testid="lead-keywords-input"
                />
              </div>
              {leadKeywords.trim() && (
                <div className="flex flex-wrap gap-1.5">
                  {parsedLeadKeywords.map((k) => (
                    <span key={k.toLowerCase()} className="inline-flex items-center text-xs font-mono px-2 py-1 rounded bg-[#002FA7]/10 text-[#002FA7]">
                      {k.toLowerCase()}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setKeywordsDialogOpen(false)}>Annuler</Button>
                <Button className="bg-[#002FA7] hover:bg-[#002277]" onClick={handleSaveKeywords}
                  disabled={savingKeywords} data-testid="save-lead-keywords-btn">
                  {savingKeywords ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
